/**
 * FreshCards ModeTheme Context
 * Provides theme management and switching functionality.
 *
 * Two orthogonal dimensions are managed here:
 *  1. *Mode*  — light / dark (system-level contrast)
 *  2. *Theme*  — product-level palette (FreshCards Slate / FreshVibe Studio)
 *
 * The theme is applied on top of the mode theme by overriding the brand-aware
 * tokens (accent, border.focused). All other tokens (spacing, radii, etc.)
 * come from the base mode theme.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { ModeTheme, ModeName, modes, Theme, ThemeId, themes, DEFAULT_THEME } from './theme-config';

interface ThemeContextType {
  mode: ModeTheme;
  modeName: ModeName;
  theme: Theme;
  themeId: ThemeId;
  setMode: (modeName: ModeName) => void;
  setTheme: (themeId: ThemeId) => void;
  toggleMode: () => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
  /** The light/dark MODE. */
  defaultMode?: ModeName;
  /** The palette THEME. */
  defaultTheme?: ThemeId;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultMode,
  defaultTheme = DEFAULT_THEME,
}) => {
  // Default to system preference so we stay in sync with Mantine
  // (which also uses 'auto' by default). If the user has explicitly
  // saved a preference, that wins.
  const [modeName, setThemeName] = useState<ModeName>(() => {
    if (defaultMode) return defaultMode;
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const [themeId, setThemeId] = useState<ThemeId>(defaultTheme);
  const [isMounted, setIsMounted] = useState(false);

  // Initialize from localStorage on mount. The MODE's key is
  // `freshcards-theme` and the THEME's is `freshcards-skin` — the persisted
  // keys are DATA and are deliberately NOT renamed (F 480): `freshcards-theme`
  // was already the mode's key, and renaming the palette's key would reset
  // every operator's saved palette.
  useEffect(() => {
    const savedMode = localStorage.getItem('freshcards-theme') as ModeName | null;
    const savedTheme = localStorage.getItem('freshcards-skin') as ThemeId | null;

    if (savedMode && modes[savedMode]) {
      setThemeName(savedMode);
    }
    if (savedTheme && themes[savedTheme]) {
      setThemeId(savedTheme);
    }
    setIsMounted(true);
  }, []);

  // Apply mode theme + theme to CSS custom properties
  useEffect(() => {
    if (!isMounted) return;

    const baseTheme = modes[modeName];
    const theme = themes[themeId];
    const root = document.documentElement;

    // 1. Apply base mode theme — sets every category at once
    Object.entries(baseTheme.colors).forEach(([category, colors]) => {
      Object.entries(colors).forEach(([name, value]) => {
        const cssVar = `--fc-${category}-${name.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
        root.style.setProperty(cssVar, value as string);
      });
    });

    // 1b. F 110 — theme palette overrides. A Theme that carries `palette`
    // (or `darkPalette` in dark mode) fully repaints the app; tokens absent
    // from the theme fall back per-token to the base ModeTheme written above.
    // Themes without a palette behave exactly as before F 110 (accent-only).
    const themePalette = modeName === 'dark' ? (theme.darkPalette ?? theme.palette) : theme.palette;
    if (themePalette) {
      const overrides: Array<[string, string | undefined]> = [
        ['--fc-bg-primary',        themePalette.background],
        ['--fc-bg-secondary',      themePalette.surfaceAlt],
        ['--fc-bg-tertiary',       themePalette.surfaceAlt],
        ['--fc-bg-hover',          themePalette.hover ?? themePalette.surfaceAlt],
        ['--fc-bg-surface',        themePalette.surface],
        ['--fc-bg-sidebar',        themePalette.sidebar],
        ['--fc-text-primary',      themePalette.text],
        ['--fc-text-secondary',    themePalette.textMuted],
        ['--fc-text-hint',         themePalette.textSubtle],
        ['--fc-text-muted',        themePalette.textMuted ?? themePalette.textSubtle],
        ['--fc-text-inverse',      themePalette.textInverse],
        ['--fc-text-disabled',     themePalette.borderStrong],
        ['--fc-border-primary',    themePalette.border],
        ['--fc-border-secondary',  themePalette.borderStrong],
        ['--fc-border-tertiary',   themePalette.accentSubtle],
        ['--fc-accent-muted',      themePalette.accentSubtle],
        ['--fc-status-success',    themePalette.success],
        ['--fc-status-warning',    themePalette.warning],
        ['--fc-status-pending',    themePalette.warning],
        ['--fc-status-error',      themePalette.danger],
        ['--fc-status-danger',     themePalette.danger],
        ['--fc-border-error',      themePalette.danger],
        ['--fc-status-info',       themePalette.info],
      ];
      for (const [cssVar, value] of overrides) {
        if (value) root.style.setProperty(cssVar, value);
      }
    }

    // 2. Overlay the theme on top — only brand-aware tokens
    // F 82: pick per-mode theme overrides when in dark mode (lifted accent)
    // F 110: accent is written LAST so it wins over any accentSubtle above.
    const accentToApply = modeName === 'dark' && theme.darkAccent ? theme.darkAccent : theme.accent;
    const borderFocusedToApply = modeName === 'dark' && theme.darkBorderFocused ? theme.darkBorderFocused : theme.borderFocused;
    const primaryGradientToApply = modeName === 'dark' && theme.darkPrimaryGradient ? theme.darkPrimaryGradient : theme.primaryGradient;
    Object.entries(accentToApply).forEach(([name, value]) => {
      const cssVar = `--fc-accent-${name}`;
      root.style.setProperty(cssVar, value);
    });
    root.style.setProperty('--fc-border-focused', borderFocusedToApply);
    root.style.setProperty('--fc-theme-id', theme.id);
    root.style.setProperty(
      '--fc-primary-gradient',
      `linear-gradient(135deg, ${primaryGradientToApply[0]} 0%, ${primaryGradientToApply[1]} 100%)`
    );

    // 3. Structural tokens (typography, spacing, radii, shadows, z, motion)
    Object.entries(baseTheme.typography.fontFamily).forEach(([name, value]) => {
      root.style.setProperty(`--fc-font-family-${name}`, value);
    });
    // F 211 D — fontSize / fontWeight / lineHeight were defined in
    // theme-config and never published here. Every `var(--fc-font-size-*)`
    // in the codebase therefore resolved to nothing and the running app
    // fell back to the browser's 16px default with Mantine's grey body
    // colour — which is why a 151-token design system existed and the UI
    // still looked unstyled. The scale was already correct; it was simply
    // never delivered. This is the single highest-leverage line in Item D,
    // and it adds no new design language — it publishes the existing one.
    Object.entries(baseTheme.typography.fontSize).forEach(([name, value]) => {
      root.style.setProperty(`--fc-font-size-${name}`, value);
    });
    Object.entries(baseTheme.typography.fontWeight).forEach(([name, value]) => {
      root.style.setProperty(`--fc-font-weight-${name}`, String(value));
    });
    Object.entries(baseTheme.typography.lineHeight).forEach(([name, value]) => {
      root.style.setProperty(`--fc-line-height-${name}`, String(value));
    });
    // Semantic aliases, so a component asks for a ROLE (--fc-type-body)
    // rather than guessing a size. h3 deliberately aliases lg: this scale
    // has no distinct 15px heading step and inventing one would put a
    // value in the scale that no measured size justified.
    root.style.setProperty('--fc-type-h1', baseTheme.typography.fontSize['2xl']);
    root.style.setProperty('--fc-type-h2', baseTheme.typography.fontSize.xl);
    root.style.setProperty('--fc-type-h3', baseTheme.typography.fontSize.lg);
    root.style.setProperty('--fc-type-body', baseTheme.typography.fontSize.base);
    root.style.setProperty('--fc-type-small', baseTheme.typography.fontSize.sm);
    root.style.setProperty('--fc-type-caption', baseTheme.typography.fontSize.xs);
    root.style.setProperty('--fc-type-display', baseTheme.typography.fontSize['3xl']);
    root.style.setProperty('--fc-leading-heading', String(baseTheme.typography.lineHeight.tight));
    root.style.setProperty('--fc-leading-body', String(baseTheme.typography.lineHeight.normal));

    Object.entries(baseTheme.spacing).forEach(([name, value]) => {
      root.style.setProperty(`--fc-space-${name}`, value);
    });
    Object.entries(baseTheme.borderRadius).forEach(([name, value]) => {
      root.style.setProperty(`--fc-radius-${name}`, value);
    });
    Object.entries(baseTheme.shadows).forEach(([name, value]) => {
      root.style.setProperty(`--fc-shadow-${name}`, value);
    });
    Object.entries(baseTheme.zIndex).forEach(([name, value]) => {
      root.style.setProperty(`--fc-z-${name}`, value.toString());
    });
    Object.entries(baseTheme.animations.duration).forEach(([name, value]) => {
      root.style.setProperty(`--fc-animation-duration-${name}`, value);
    });
    Object.entries(baseTheme.animations.easing).forEach(([name, value]) => {
      root.style.setProperty(`--fc-animation-easing-${name}`, value);
    });

    // 4. Data attributes for any CSS rules that need to switch on theme/theme
    root.setAttribute('data-theme', modeName);
    root.setAttribute('data-theme-palette', themeId);
    root.setAttribute('data-product', theme.product);

    // 5. Sync Mantine's color scheme so the two never drift out of sync
    // (Mantine provides useMantineColorScheme but we don't have access to
    // the hook here; use the data attribute it reads as well as the
    // persisted localStorage key that MantineProvider watches).
    if (modeName === 'dark') {
      root.setAttribute('data-mantine-color-scheme', 'dark');
      localStorage.setItem('mantine-color-scheme-value', 'dark');
    } else {
      root.setAttribute('data-mantine-color-scheme', 'light');
      localStorage.setItem('mantine-color-scheme-value', 'light');
    }

    // 6. Persist
    localStorage.setItem('freshcards-theme', modeName);
    localStorage.setItem('freshcards-skin', themeId);
  }, [modeName, themeId, isMounted]);

  const toggleMode = () => {
    setThemeName(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  const value: ThemeContextType = {
    mode: modes[modeName],
    modeName,
    theme: themes[themeId],
    themeId,
    setMode: setThemeName,
    setTheme: setThemeId,
    toggleMode,
    isDark: modeName === 'dark',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const useModeColors = () => {
  const { mode } = useTheme();
  return mode.colors;
};

export const useModeTypography = () => {
  const { mode } = useTheme();
  return mode.typography;
};

export const useModeSpacing = () => {
  const { mode } = useTheme();
  return mode.spacing;
};

export const useModeBorderRadius = () => {
  const { mode } = useTheme();
  return mode.borderRadius;
};

export const useModeShadows = () => {
  const { mode } = useTheme();
  return mode.shadows;
};

export const useModeBreakpoints = () => {
  const { mode } = useTheme();
  return mode.breakpoints;
};